import { useCallback, useEffect, useState } from 'react';

import { analytics } from 'analytics';
import AdditionalProfileInfoForm from 'components/AdditionalProfileInfoForm';
import AuthenticationOptions from 'components/AuthenticationOptions';
import Dialog from 'ui/Dialog';
import { useCurrentFormSankeyId } from 'utils/form';
import { useCurrentUser } from 'utils/user';

import StepDescription from '../StepDescription';

enum Step {
  Authenticate,
  ProfileInfo,
}

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  analyticsName: string;
  defaultAction: 'sign-in' | 'sign-up';
  portalRef?: React.RefObject<HTMLElement>;
};

// TODO: Rename to AuthenticationDialog
const AuthenticationModal: React.FC<Props> = ({
  open,
  setOpen,
  title,
  analyticsName,
  defaultAction,
  portalRef,
}) => {
  const [activeStep, setActiveStep] = useState<Step>();
  const sankeyId = useCurrentFormSankeyId();
  const { user } = useCurrentUser();

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
    if (!open) {
      analytics.track(`${analyticsName} - Close`);
    }
  }, []);

  // Track the step but only when the modal is opened
  useEffect(() => {
    if (open) {
      if (activeStep === Step.Authenticate) {
        analytics.track(`${analyticsName} - Step - Authenticate`);
      } else if (activeStep === Step.ProfileInfo) {
        analytics.track(`${analyticsName} - Step - Profile Info`);
      }
    }
  }, [open, activeStep]);

  // Close the modal if the user has completed the profile info
  const shouldClose = open && user?.default_team?.has_user_completed_team_info;

  // Jump to correct step whenever user state changes
  useEffect(() => {
    if (shouldClose) {
      handleOpenChange(false);
    } else if (user?.is_authenticated) {
      setActiveStep(Step.ProfileInfo);
    } else {
      setActiveStep(Step.Authenticate);
    }
  }, [shouldClose, user?.is_authenticated]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e) => handleOpenChange(e.open)}
      size="sm"
    >
      <Dialog.Content portalRef={portalRef}>
        <Dialog.Header>{title}</Dialog.Header>
        <Dialog.Body>
          {activeStep === Step.Authenticate && (
            <>
              <StepDescription>
                {sankeyId
                  ? 'We will store this diagram on our server, allowing you to access it at any time.'
                  : 'Please select your preferred authentication method.'}
              </StepDescription>
              <AuthenticationOptions defaultAction={defaultAction} />
            </>
          )}
          {activeStep === Step.ProfileInfo && (
            <>
              <StepDescription>
                Please fill in your profile information to continue. Don't
                worry, we won't share it with anyone.
              </StepDescription>
              <AdditionalProfileInfoForm
                onSubmit={() => {
                  analytics.track(`${analyticsName} - Complete`);
                  handleOpenChange(false);
                }}
                submitLabel="Finish"
              />
            </>
          )}
        </Dialog.Body>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AuthenticationModal;
